import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { fetchProjects } from '../api/endpoints';
import type { Project } from '../api/types';
import { useResource } from '../hooks/useResource';
import type { Resource } from '../hooks/useResource';

/**
 * `GET /api/v1/projects`, fetched ONCE for the whole shell.
 *
 * 🔴 It has two readers — the project rail (and its mobile dropdown) and the backlog's filter bar —
 * and before this provider each fetched its own copy. Two copies of one list is two chances to
 * disagree: the rail rendering five projects while the filter select showed an error, on the same
 * screen, both looking authoritative. ⇒ One resource, one error, one retry.
 *
 * ⭐ The whole `Resource` is exposed, not `data ?? []`. A reader that only ever sees an array cannot
 * tell "no projects exist" from "we could not ask", and that collapse is the bug this app spends
 * most of its comments refusing.
 */
const ProjectsContext = createContext<Resource<Project[]> | null>(null);

export function ProjectsProvider({ children }: { children: ReactNode }) {
  const resource = useResource<Project[]>((signal) => fetchProjects(signal), []);
  return <ProjectsContext.Provider value={resource}>{children}</ProjectsContext.Provider>;
}

export function useProjects(): Resource<Project[]> {
  const context = useContext(ProjectsContext);
  if (!context) throw new Error('useProjects must be used inside ProjectsProvider');
  return context;
}

/** `kind` is optional on the wire — treat only an explicit 'research' as research. */
export function isResearchProject(project: Project): boolean {
  return project.kind === 'research';
}
